/**
 * Expo Organizer Phase 2 (Feb 2026 R37) — floor plan / tickets /
 * sessions / kiosk tabs, plus per-workspace Cashfree config.
 *
 * Kept as ONE file so the whole Phase-2 UI ships as a single
 * componentised block that slots into <ExpoDetail /> without touching
 * its layout. Each sub-tab is a small function component with a local
 * <load /> effect so they only fetch when actually visible.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  LayoutGrid, Ticket, CalendarClock, ScanLine, Plus, Trash2, Copy,
  CheckCircle2, XCircle, CreditCard, RefreshCw, Zap,
} from "lucide-react";

const T = { wide: "border border-border bg-background", muted: "bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold" };
const BTN = "rounded-none";
const CHIP = "chip text-[10px]";

// ═════════════════════════════════════════════════════════════════════
// Public entry point — 4-tab Phase 2 block
// ═════════════════════════════════════════════════════════════════════

export default function Phase2Panel({ wsId, expoId }) {
  const [tab, setTab] = useState("floor");
  const tabs = [
    { key: "floor",     label: "Floor plan",     icon: LayoutGrid },
    { key: "amenities", label: "Amenities",      icon: Zap },
    { key: "tickets",   label: "Tickets",        icon: Ticket },
    { key: "sessions",  label: "Sessions",       icon: CalendarClock },
    { key: "kiosk",     label: "Check-in kiosk", icon: ScanLine },
  ];
  return (
    <div className="mt-6 space-y-3" data-testid="expo-phase2">
      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {tabs.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-2 text-sm border-b-2 inline-flex items-center gap-1.5 whitespace-nowrap ${
                tab === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`expo-phase2-tab-${t.key}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>
      {tab === "floor"     && <FloorPlanTab wsId={wsId} expoId={expoId} />}
      {tab === "amenities" && <AmenitiesTab wsId={wsId} expoId={expoId} />}
      {tab === "tickets"   && <TicketsTab   wsId={wsId} expoId={expoId} />}
      {tab === "sessions"  && <SessionsTab  wsId={wsId} expoId={expoId} />}
      {tab === "kiosk"     && <KioskTab     wsId={wsId} expoId={expoId} />}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// 1 · Floor plan — drag-drop grid of booths
//     Iter Feb 2026 R38 · Full palette-based drag-drop replaces the
//     form-based x/y editor:
//        • Palette on the left with 4 booth-size templates.
//        • Drag a palette item onto the canvas → creates a booth at
//          the drop position (snapped to grid cells).
//        • Existing booths are draggable to re-position (mouse) or
//          movable via arrow keys after focusing.
//        • Corner handle on selected booth resizes it.
//        • Click a booth to select; Enter/dbl-click opens the dialog;
//          Delete key removes it.
// ═════════════════════════════════════════════════════════════════════

const BOOTH_STATUS_COLOR = {
  available: "bg-emerald-100 text-emerald-800 border-emerald-400",
  reserved:  "bg-amber-100 text-amber-800 border-amber-400",
  sold:      "bg-rose-100 text-rose-800 border-rose-400",
  blocked:   "bg-neutral-200 text-neutral-700 border-neutral-400",
};

const PALETTE = [
  { w: 1, h: 1, label: "1×1" },
  { w: 2, h: 2, label: "2×2" },
  { w: 3, h: 2, label: "3×2" },
  { w: 3, h: 3, label: "3×3" },
];

const MAX_X = 30, MAX_Y = 20;

function FloorPlanTab({ wsId, expoId }) {
  const [booths, setBooths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // booth or "new"
  const [selectedId, setSelectedId] = useState(null);
  const [drag, setDrag] = useState(null);
  // drag shapes:
  //   { kind: "new", w, h }                        — dragging from palette
  //   { kind: "move", id, w, h, ox, oy }           — dragging existing booth
  //   { kind: "resize", id, x, y }                 — resizing existing booth

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/workspaces/${wsId}/organizer/expos/${expoId}/booths`);
      setBooths(r.data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load booths")); }
    finally { setLoading(false); }
  }, [wsId, expoId]);
  useEffect(() => { load(); }, [load]);

  // Convert a click on the canvas to grid (col, row) — 0-indexed.
  const toGrid = (canvas, e) => {
    const rect = canvas.getBoundingClientRect();
    const relX = e.clientX - rect.left;
    const relY = e.clientY - rect.top;
    const col = Math.max(0, Math.min(MAX_X - 1, Math.floor(relX / (rect.width / MAX_X))));
    const row = Math.max(0, Math.min(MAX_Y - 1, Math.floor(relY / (rect.height / MAX_Y))));
    return { x: col, y: row };
  };

  const canvasRef = useState(null);

  const onCanvasDrop = async (e) => {
    e.preventDefault();
    if (!drag) return;
    const { x, y } = toGrid(e.currentTarget, e);
    if (drag.kind === "new") {
      const nextCode = `B-${(booths.length + 1).toString().padStart(2, "0")}`;
      try {
        await api.post(`/workspaces/${wsId}/organizer/expos/${expoId}/booths`,
                        { code: nextCode, x, y, w: drag.w, h: drag.h, status: "available" });
        toast.success(`Booth ${nextCode} created`);
        load();
      } catch (err) { toast.error(apiErrorMessage(err, "Create failed")); }
    } else if (drag.kind === "move") {
      const nx = Math.max(0, Math.min(MAX_X - drag.w, x - (drag.ox || 0)));
      const ny = Math.max(0, Math.min(MAX_Y - drag.h, y - (drag.oy || 0)));
      try {
        await api.patch(`/organizer-booths/${drag.id}`, { x: nx, y: ny });
        setBooths((prev) => prev.map((b) => b.id === drag.id ? { ...b, x: nx, y: ny } : b));
      } catch (err) { toast.error(apiErrorMessage(err, "Move failed")); }
    } else if (drag.kind === "resize") {
      const nw = Math.max(1, Math.min(MAX_X - drag.x, (x - drag.x) + 1));
      const nh = Math.max(1, Math.min(MAX_Y - drag.y, (y - drag.y) + 1));
      try {
        await api.patch(`/organizer-booths/${drag.id}`, { w: nw, h: nh });
        setBooths((prev) => prev.map((b) => b.id === drag.id ? { ...b, w: nw, h: nh } : b));
      } catch (err) { toast.error(apiErrorMessage(err, "Resize failed")); }
    }
    setDrag(null);
  };

  const del = async (b) => {
    if (!window.confirm(`Delete booth "${b.code}"?`)) return;
    try {
      await api.delete(`/organizer-booths/${b.id}`);
      toast.success("Booth deleted"); setSelectedId(null); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  // Keyboard: arrow-move, delete, enter-open
  useEffect(() => {
    if (!selectedId) return undefined;
    const handler = async (e) => {
      const b = booths.find((x) => x.id === selectedId);
      if (!b) return;
      const nudges = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
      if (nudges[e.key]) {
        e.preventDefault();
        const [dx, dy] = nudges[e.key];
        const nx = Math.max(0, Math.min(MAX_X - b.w, b.x + dx));
        const ny = Math.max(0, Math.min(MAX_Y - b.h, b.y + dy));
        try {
          await api.patch(`/organizer-booths/${b.id}`, { x: nx, y: ny });
          setBooths((prev) => prev.map((x) => x.id === b.id ? { ...x, x: nx, y: ny } : x));
        } catch { /* ignore */ }
      } else if (e.key === "Enter") {
        setEditing(b);
      } else if (e.key === "Delete" || e.key === "Backspace") {
        del(b);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, booths]);

  return (
    <div className="space-y-3" data-testid="floor-plan-tab">
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          {booths.length} booth{booths.length === 1 ? "" : "s"}. Drag a size from the palette → drop on canvas. Click a booth to select; arrows to nudge, Delete to remove, Enter to open details.
        </div>
        <Button size="sm" variant="outline" className={BTN} onClick={() => setEditing("new")} data-testid="booth-new">
          <Plus className="w-3.5 h-3.5 mr-1" /> New (form)
        </Button>
      </div>
      <div className="flex gap-3">
        {/* Palette */}
        <div className="w-32 border border-border p-2 space-y-2 bg-muted/20 shrink-0" data-testid="booth-palette">
          <div className={T.muted}>Palette</div>
          {PALETTE.map((p) => (
            <div
              key={p.label}
              draggable
              onDragStart={() => setDrag({ kind: "new", w: p.w, h: p.h })}
              onDragEnd={() => setDrag(null)}
              className="border border-primary/40 bg-primary/10 text-primary text-[11px] font-semibold px-2 py-3 cursor-grab hover:bg-primary/20 text-center"
              data-testid={`palette-${p.label}`}
              title={`Drag to canvas · ${p.label}`}
            >
              {p.label}
            </div>
          ))}
        </div>
        {/* Canvas */}
        {loading ? <div className="flex-1 text-sm text-muted-foreground">Loading…</div> : (
          <div
            ref={canvasRef}
            className="flex-1 border border-border bg-muted/20 p-1 overflow-auto"
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
            onDrop={onCanvasDrop}
            onClick={(e) => { if (e.currentTarget === e.target) setSelectedId(null); }}
            data-testid="floor-plan-canvas"
            style={{ minHeight: 300 }}
          >
            <div
              className="grid gap-0.5"
              style={{
                gridTemplateColumns: `repeat(${MAX_X}, minmax(22px, 1fr))`,
                gridAutoRows: "26px",
                width: "100%",
              }}
            >
              {booths.map((b) => {
                const selected = b.id === selectedId;
                return (
                  <div
                    key={b.id}
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); setSelectedId(b.id); }}
                    onDoubleClick={() => setEditing(b)}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = "move";
                      // Approx offset (how many cells into the booth was the pointer)
                      const rect = e.currentTarget.getBoundingClientRect();
                      const ox = Math.floor((e.clientX - rect.left) / (rect.width / (b.w || 1)));
                      const oy = Math.floor((e.clientY - rect.top) / (rect.height / (b.h || 1)));
                      setDrag({ kind: "move", id: b.id, w: b.w || 1, h: b.h || 1, ox, oy });
                    }}
                    onDragEnd={() => setDrag(null)}
                    className={`relative text-[9px] font-semibold border ${BOOTH_STATUS_COLOR[b.status] || "bg-white"} cursor-grab select-none ${selected ? "ring-2 ring-primary" : "hover:ring-1 hover:ring-primary/50"}`}
                    style={{
                      gridColumn: `${(b.x || 0) + 1} / span ${Math.max(1, b.w || 1)}`,
                      gridRow: `${(b.y || 0) + 1} / span ${Math.max(1, b.h || 1)}`,
                    }}
                    title={`${b.code} · ${b.status}${b.assigned_exhibitor_name ? ` · ${b.assigned_exhibitor_name}` : ""}${b.total_amount ? ` · ${b.total_currency || "INR"} ${b.total_amount}` : ""}`}
                    data-testid={`booth-${b.code}`}
                  >
                    <div className="p-0.5 truncate">{b.code}</div>
                    {selected && (
                      <>
                        {/* Corner resize handle */}
                        <div
                          draggable
                          onDragStart={(e) => {
                            e.stopPropagation();
                            e.dataTransfer.effectAllowed = "move";
                            setDrag({ kind: "resize", id: b.id, x: b.x || 0, y: b.y || 0 });
                          }}
                          onDragEnd={() => setDrag(null)}
                          className="absolute -bottom-1 -right-1 w-3 h-3 bg-primary border border-white cursor-nwse-resize"
                          data-testid={`booth-resize-${b.code}`}
                          title="Drag to resize"
                        />
                        <button
                          onClick={(e) => { e.stopPropagation(); del(b); }}
                          className="absolute -top-1 -right-1 w-4 h-4 bg-white border border-rose-400 text-rose-600 text-[10px] leading-none flex items-center justify-center hover:bg-rose-100"
                          title="Delete"
                          data-testid={`booth-delete-${b.code}`}
                        >×</button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-3 text-[10px] flex items-center gap-3 text-muted-foreground flex-wrap">
              <span>{`Grid ${MAX_X}×${MAX_Y}. Drag palette items to add; drag booths to move.`}</span>
              {Object.entries(BOOTH_STATUS_COLOR).map(([s, cls]) => (
                <span key={s} className="inline-flex items-center gap-1">
                  <span className={`w-3 h-3 border ${cls}`}></span>{s}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      {editing && (
        <BoothDialog
          wsId={wsId} expoId={expoId}
          booth={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          onDelete={editing !== "new" ? () => del(editing) : null}
        />
      )}
    </div>
  );
}

function BoothDialog({ wsId, expoId, booth, onClose, onSaved, onDelete }) {
  const [form, setForm] = useState(booth || {
    code: "", x: 0, y: 0, w: 2, h: 2, capacity: null, status: "available",
    assigned_exhibitor_name: "", notes: "",
  });
  const [amenities, setAmenities] = useState([]);
  // Selected amenities: {amenity_id: qty}
  const [selected, setSelected] = useState(() => {
    const m = {};
    (booth?.amenities || []).forEach((a) => { m[a.amenity_id] = a.qty || 1; });
    return m;
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    api.get(`/workspaces/${wsId}/organizer/expos/${expoId}/amenities`)
      .then((r) => setAmenities((r.data || []).filter((a) => a.active !== false)))
      .catch(() => setAmenities([]));
  }, [wsId, expoId]);

  const total = amenities.reduce((s, a) => s + ((selected[a.id] || 0) * Number(a.price_amount || 0)), 0);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form, x: +form.x, y: +form.y, w: +form.w, h: +form.h,
                          capacity: form.capacity == null || form.capacity === "" ? null : +form.capacity };
      let saved;
      if (booth) { saved = (await api.patch(`/organizer-booths/${booth.id}`, payload)).data; }
      else       { saved = (await api.post(`/workspaces/${wsId}/organizer/expos/${expoId}/booths`, payload)).data; }
      // Attach amenities in a second call so pricing is server-computed.
      const amPayload = Object.entries(selected)
        .filter(([, q]) => q > 0)
        .map(([amenity_id, qty]) => ({ amenity_id, qty: +qty }));
      if (amPayload.length > 0 || (booth?.amenities || []).length > 0) {
        await api.put(`/organizer-booths/${saved.id}/amenities`, { amenities: amPayload });
      }
      toast.success(booth ? "Booth updated" : "Booth created");
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="booth-dialog" className="max-w-2xl">
        <DialogHeader><DialogTitle>{booth ? "Edit booth" : "New booth"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="label-eyebrow">Code *</Label>
            <Input className={`${BTN} h-9`} value={form.code} onChange={(e) => set("code", e.target.value)} data-testid="booth-code" /></div>
          <div><Label className="label-eyebrow">Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className={`${BTN} h-9`} data-testid="booth-status"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.keys(BOOTH_STATUS_COLOR).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="label-eyebrow">X (0-{29})</Label>
            <Input type="number" className={`${BTN} h-9`} value={form.x} onChange={(e) => set("x", e.target.value)} data-testid="booth-x" /></div>
          <div><Label className="label-eyebrow">Y (0-{19})</Label>
            <Input type="number" className={`${BTN} h-9`} value={form.y} onChange={(e) => set("y", e.target.value)} data-testid="booth-y" /></div>
          <div><Label className="label-eyebrow">Width</Label>
            <Input type="number" className={`${BTN} h-9`} value={form.w} onChange={(e) => set("w", e.target.value)} /></div>
          <div><Label className="label-eyebrow">Height</Label>
            <Input type="number" className={`${BTN} h-9`} value={form.h} onChange={(e) => set("h", e.target.value)} /></div>
          <div><Label className="label-eyebrow">Capacity</Label>
            <Input type="number" className={`${BTN} h-9`} value={form.capacity ?? ""} onChange={(e) => set("capacity", e.target.value)} /></div>
          <div><Label className="label-eyebrow">Assigned to</Label>
            <Input className={`${BTN} h-9`} value={form.assigned_exhibitor_name || ""} onChange={(e) => set("assigned_exhibitor_name", e.target.value)} placeholder="Exhibitor name" /></div>
          <div className="col-span-2"><Label className="label-eyebrow">Notes</Label>
            <Textarea className={BTN} rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
          {/* Iter Feb 2026 R38 · Amenity selector — quantity per catalog
               entry. Total is server-computed on save from the current
               catalog price (so past bookings keep their snapshot even
               when the catalog price changes later). */}
          <div className="col-span-2 space-y-1">
            <Label className="label-eyebrow">Amenities (add-ons)</Label>
            {amenities.length === 0 ? (
              <div className="text-xs text-muted-foreground italic">
                No amenities in catalog. Add some in the Amenities tab.
              </div>
            ) : (
              <div className="border border-border max-h-48 overflow-y-auto" data-testid="booth-amenity-picker">
                {amenities.map((a) => (
                  <div key={a.id} className="flex items-center gap-2 px-2 py-1 border-b border-border text-sm hover:bg-muted/20">
                    <div className="flex-1 truncate">
                      <span className="font-mono text-[10px] text-muted-foreground mr-2">{a.code}</span>
                      {a.name}
                      <span className="text-[10px] text-muted-foreground ml-2">{a.currency} {Number(a.price_amount).toLocaleString()}{a.unit ? ` · ${a.unit}` : ""}</span>
                    </div>
                    <Input
                      type="number" min="0"
                      className={`${BTN} h-7 w-16 text-xs text-center`}
                      value={selected[a.id] || 0}
                      onChange={(e) => setSelected((s) => ({ ...s, [a.id]: Math.max(0, +e.target.value || 0) }))}
                      data-testid={`booth-amenity-qty-${a.code}`}
                    />
                  </div>
                ))}
                <div className="px-2 py-1 bg-muted/40 text-xs flex items-center justify-between">
                  <span>Estimated total</span>
                  <span className="font-mono font-semibold" data-testid="booth-amenity-total">
                    {(amenities[0]?.currency || "INR")} {total.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="flex items-center justify-between">
          {onDelete && <Button variant="ghost" className={`${BTN} text-rose-600`} onClick={onDelete}><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>}
          <div className="flex gap-2 ml-auto">
            <Button variant="ghost" className={BTN} onClick={onClose}>Cancel</Button>
            <Button className={BTN} onClick={submit} disabled={busy || !form.code} data-testid="booth-save">
              {busy ? "Saving…" : "Save"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═════════════════════════════════════════════════════════════════════
// 2 · Ticket tiers + Cashfree config
// ═════════════════════════════════════════════════════════════════════

function TicketsTab({ wsId, expoId }) {
  const [tiers, setTiers] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [cfgOpen, setCfgOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, c] = await Promise.all([
        api.get(`/workspaces/${wsId}/organizer/expos/${expoId}/ticket-tiers`),
        api.get(`/workspaces/${wsId}/organizer/payment-gateway/cashfree`),
      ]);
      setTiers(t.data || []); setCfg(c.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load tickets")); }
    finally { setLoading(false); }
  }, [wsId, expoId]);
  useEffect(() => { load(); }, [load]);

  const del = async (t) => {
    if (!window.confirm(`Delete tier "${t.name}"? Sold tickets → tier will be soft-disabled instead.`)) return;
    try {
      await api.delete(`/organizer-ticket-tiers/${t.id}`);
      toast.success("Tier removed"); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const publicUrl = `${window.location.origin}/public/expo/${expoId}/tickets`;

  return (
    <div className="space-y-3" data-testid="tickets-tab">
      {/* Cashfree config banner */}
      <div className={`${T.wide} p-3 flex items-center justify-between gap-3 flex-wrap`}
            data-testid="cashfree-cfg-banner">
        <div className="flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-primary" />
          {cfg?.configured ? (
            <div className="text-xs">
              Cashfree <b>{cfg.mode}</b> connected · <span className="font-mono">…{cfg.client_id_last4}</span>
              {!cfg.enabled && <span className="ml-2 chip text-[10px] bg-rose-100 border-rose-300 text-rose-800">disabled</span>}
            </div>
          ) : (
            <div className="text-xs text-amber-900">
              Cashfree isn&apos;t configured for this workspace — buyers can&apos;t pay yet.
            </div>
          )}
        </div>
        <Button size="sm" variant="outline" className={BTN} onClick={() => setCfgOpen(true)} data-testid="cashfree-cfg-open">
          {cfg?.configured ? "Update keys" : "Connect Cashfree"}
        </Button>
      </div>

      {/* Public URL */}
      <div className={`${T.wide} p-3 flex items-center gap-2 text-xs`}>
        <span className="label-eyebrow">Public buy link</span>
        <code className="flex-1 truncate font-mono text-[11px]">{publicUrl}</code>
        <button className="text-primary hover:underline text-[11px] inline-flex items-center gap-1"
                 onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Link copied"); }}
                 data-testid="tickets-copy-link">
          <Copy className="w-3 h-3" /> Copy
        </button>
        <Link to={`/public/expo/${expoId}/tickets`} target="_blank" className="text-primary hover:underline text-[11px]">Open</Link>
      </div>

      {/* Tier list */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">{tiers.length} tier{tiers.length === 1 ? "" : "s"}</div>
        <Button size="sm" className={BTN} onClick={() => setEditing("new")} data-testid="tier-new">
          <Plus className="w-3.5 h-3.5 mr-1" /> New tier
        </Button>
      </div>
      {loading ? <div className="text-sm text-muted-foreground">Loading…</div> : tiers.length === 0 ? (
        <div className="border border-border p-6 text-center text-xs text-muted-foreground italic">
          No ticket tiers yet — add one to start selling.
        </div>
      ) : (
        <div className={T.wide}>
          <div className={`grid grid-cols-12 gap-2 px-3 py-2 ${T.muted}`}>
            <div className="col-span-4">Tier</div>
            <div className="col-span-2">Price</div>
            <div className="col-span-2">Stock</div>
            <div className="col-span-2">Sold</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {tiers.map((t) => (
            <div key={t.id} className="grid grid-cols-12 gap-2 px-3 py-2 items-center border-t border-border text-sm hover:bg-muted/20"
                  data-testid={`tier-row-${t.id}`}>
              <div className="col-span-4">
                <div className="font-semibold">{t.name}</div>
                {t.description && <div className="text-[11px] text-muted-foreground truncate">{t.description}</div>}
              </div>
              <div className="col-span-2 font-mono text-xs">{t.currency || "INR"} {Number(t.price_amount).toLocaleString()}</div>
              <div className="col-span-2 text-xs">{t.stock ?? "∞"}</div>
              <div className="col-span-2 text-xs">
                {t.sold || 0}
                {!t.active && <span className={`ml-2 ${CHIP} bg-neutral-200 border-neutral-400 text-neutral-700`}>disabled</span>}
              </div>
              <div className="col-span-2 flex items-center justify-end gap-1">
                <Button size="sm" variant="outline" className={`${BTN} h-7 text-[10px]`} onClick={() => setEditing(t)} data-testid={`tier-edit-${t.id}`}>Edit</Button>
                <Button size="sm" variant="ghost" className={`${BTN} h-7 w-7 p-0 text-muted-foreground hover:text-destructive`} onClick={() => del(t)} data-testid={`tier-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <TierDialog wsId={wsId} expoId={expoId}
                     tier={editing === "new" ? null : editing}
                     onClose={() => setEditing(null)}
                     onSaved={() => { setEditing(null); load(); }} />
      )}
      {cfgOpen && (
        <CashfreeCfgDialog wsId={wsId} cfg={cfg}
                             onClose={() => setCfgOpen(false)}
                             onSaved={() => { setCfgOpen(false); load(); }} />
      )}
    </div>
  );
}

function TierDialog({ wsId, expoId, tier, onClose, onSaved }) {
  const [form, setForm] = useState(tier || {
    name: "", description: "", price_amount: 0, currency: "INR",
    stock: null, active: true,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form,
        price_amount: +form.price_amount,
        stock: form.stock == null || form.stock === "" ? null : +form.stock };
      if (tier) await api.patch(`/organizer-ticket-tiers/${tier.id}`, payload);
      else       await api.post(`/workspaces/${wsId}/organizer/expos/${expoId}/ticket-tiers`, payload);
      toast.success(tier ? "Tier updated" : "Tier created");
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="tier-dialog">
        <DialogHeader><DialogTitle>{tier ? "Edit tier" : "New ticket tier"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="label-eyebrow">Name *</Label>
            <Input className={`${BTN} h-9`} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="tier-name" /></div>
          <div><Label className="label-eyebrow">Description</Label>
            <Textarea className={BTN} rows={2} value={form.description || ""} onChange={(e) => set("description", e.target.value)} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="label-eyebrow">Price *</Label>
              <Input type="number" step="0.01" className={`${BTN} h-9`} value={form.price_amount} onChange={(e) => set("price_amount", e.target.value)} data-testid="tier-price" /></div>
            <div><Label className="label-eyebrow">Currency</Label>
              <Input className={`${BTN} h-9`} value={form.currency} onChange={(e) => set("currency", e.target.value.toUpperCase())} maxLength={3} /></div>
            <div><Label className="label-eyebrow">Stock</Label>
              <Input type="number" className={`${BTN} h-9`} value={form.stock ?? ""} onChange={(e) => set("stock", e.target.value)} placeholder="Unlimited" /></div>
          </div>
          <label className="text-xs flex items-center gap-2">
            <input type="checkbox" checked={form.active !== false} onChange={(e) => set("active", e.target.checked)} />
            Active (visible to buyers)
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" className={BTN} onClick={onClose}>Cancel</Button>
          <Button className={BTN} onClick={submit} disabled={busy || !form.name} data-testid="tier-save">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CashfreeCfgDialog({ wsId, cfg, onClose, onSaved }) {
  const [mode, setMode] = useState(cfg?.mode || "TEST");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [enabled, setEnabled] = useState(cfg?.enabled ?? true);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!clientId || !clientSecret) { toast.error("Client ID and Secret are required"); return; }
    setBusy(true);
    try {
      await api.put(`/workspaces/${wsId}/organizer/payment-gateway/cashfree`, {
        mode, client_id: clientId, client_secret: clientSecret,
        webhook_secret: webhookSecret || null, enabled,
      });
      toast.success("Cashfree connected");
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="cashfree-cfg-dialog">
        <DialogHeader><DialogTitle>Connect Cashfree</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-[11px] text-muted-foreground">
            Get your API keys from the Cashfree dashboard → Developers → Credentials. Keys are stored encrypted; only the last 4 characters of the Client ID are ever shown after save.
          </div>
          <div><Label className="label-eyebrow">Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className={`${BTN} h-9`} data-testid="cashfree-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="TEST">TEST (sandbox)</SelectItem>
                <SelectItem value="LIVE">LIVE (production)</SelectItem>
              </SelectContent>
            </Select></div>
          <div><Label className="label-eyebrow">Client ID *</Label>
            <Input className={`${BTN} h-9`} value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="TEST_CLIENT_ID_..." data-testid="cashfree-client-id" /></div>
          <div><Label className="label-eyebrow">Client Secret *</Label>
            <Input type="password" className={`${BTN} h-9`} value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} data-testid="cashfree-client-secret" /></div>
          <div><Label className="label-eyebrow">Webhook Secret (optional)</Label>
            <Input type="password" className={`${BTN} h-9`} value={webhookSecret} onChange={(e) => setWebhookSecret(e.target.value)} placeholder="Used to verify order.paid webhooks" /></div>
          <label className="text-xs flex items-center gap-2">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="cashfree-enabled" /> Enabled
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" className={BTN} onClick={onClose}>Cancel</Button>
          <Button className={BTN} onClick={submit} disabled={busy} data-testid="cashfree-save">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═════════════════════════════════════════════════════════════════════
// 3 · Session tracks
// ═════════════════════════════════════════════════════════════════════

function SessionsTab({ wsId, expoId }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/workspaces/${wsId}/organizer/expos/${expoId}/sessions`);
      setSessions(r.data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load sessions")); }
    finally { setLoading(false); }
  }, [wsId, expoId]);
  useEffect(() => { load(); }, [load]);

  const del = async (s) => {
    if (!window.confirm(`Delete session "${s.title}"? All RSVPs will be removed.`)) return;
    try {
      await api.delete(`/organizer-sessions/${s.id}`);
      toast.success("Session deleted"); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  // Group sessions by track for a two-lane display.
  const byTrack = sessions.reduce((acc, s) => {
    (acc[s.track_name] = acc[s.track_name] || []).push(s); return acc;
  }, {});
  const tracks = Object.keys(byTrack).sort();

  return (
    <div className="space-y-3" data-testid="sessions-tab">
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">{sessions.length} session{sessions.length === 1 ? "" : "s"} across {tracks.length} track{tracks.length === 1 ? "" : "s"}</div>
        <Button size="sm" className={BTN} onClick={() => setEditing("new")} data-testid="session-new">
          <Plus className="w-3.5 h-3.5 mr-1" /> New session
        </Button>
      </div>
      {loading ? <div className="text-sm text-muted-foreground">Loading…</div> : tracks.length === 0 ? (
        <div className="border border-border p-6 text-center text-xs text-muted-foreground italic">
          No sessions yet — add tracks + talks for your attendees to RSVP.
        </div>
      ) : (
        <div className="space-y-4">
          {tracks.map((t) => (
            <div key={t} className={T.wide}>
              <div className={`${T.muted} px-3 py-2 flex items-center justify-between`}>
                <span>Track · {t}</span>
                <span>{byTrack[t].length} session{byTrack[t].length === 1 ? "" : "s"}</span>
              </div>
              {byTrack[t].map((s) => (
                <div key={s.id} className="px-3 py-2 border-t border-border grid grid-cols-12 gap-2 items-center text-sm"
                      data-testid={`session-row-${s.id}`}>
                  <div className="col-span-4">
                    <div className="font-semibold">{s.title}</div>
                    {s.speaker_name && <div className="text-[11px] text-muted-foreground">🎤 {s.speaker_name}</div>}
                  </div>
                  <div className="col-span-2 text-xs">{s.room_name || "—"}</div>
                  <div className="col-span-3 text-xs font-mono">
                    {(s.start_at || "").slice(0, 16).replace("T", " ")}<br />→ {(s.end_at || "").slice(0, 16).replace("T", " ")}
                  </div>
                  <div className="col-span-2 text-xs">
                    <span className={CHIP}>{s.rsvp_count || 0}{s.capacity ? `/${s.capacity}` : ""} RSVP</span>
                  </div>
                  <div className="col-span-1 flex justify-end gap-1">
                    <Button size="sm" variant="outline" className={`${BTN} h-7 text-[10px]`} onClick={() => setEditing(s)}>Edit</Button>
                    <Button size="sm" variant="ghost" className={`${BTN} h-7 w-7 p-0 text-muted-foreground hover:text-destructive`} onClick={() => del(s)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      {editing && (
        <SessionDialog wsId={wsId} expoId={expoId}
                        session={editing === "new" ? null : editing}
                        onClose={() => setEditing(null)}
                        onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function SessionDialog({ wsId, expoId, session, onClose, onSaved }) {
  const [form, setForm] = useState(session || {
    track_name: "Main", title: "", description: "", speaker_name: "",
    room_name: "", start_at: "", end_at: "", capacity: null,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    if (!form.title || !form.track_name || !form.start_at || !form.end_at) {
      toast.error("Track, title, start & end are required"); return;
    }
    setBusy(true);
    try {
      const payload = { ...form,
        capacity: form.capacity == null || form.capacity === "" ? null : +form.capacity };
      if (session) await api.patch(`/organizer-sessions/${session.id}`, payload);
      else          await api.post(`/workspaces/${wsId}/organizer/expos/${expoId}/sessions`, payload);
      toast.success(session ? "Session updated" : "Session created");
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="session-dialog">
        <DialogHeader><DialogTitle>{session ? "Edit session" : "New session"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-eyebrow">Track *</Label>
              <Input className={`${BTN} h-9`} value={form.track_name} onChange={(e) => set("track_name", e.target.value)} data-testid="session-track" /></div>
            <div><Label className="label-eyebrow">Room</Label>
              <Input className={`${BTN} h-9`} value={form.room_name || ""} onChange={(e) => set("room_name", e.target.value)} /></div>
          </div>
          <div><Label className="label-eyebrow">Title *</Label>
            <Input className={`${BTN} h-9`} value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="session-title" /></div>
          <div><Label className="label-eyebrow">Description</Label>
            <Textarea className={BTN} rows={2} value={form.description || ""} onChange={(e) => set("description", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-eyebrow">Speaker</Label>
              <Input className={`${BTN} h-9`} value={form.speaker_name || ""} onChange={(e) => set("speaker_name", e.target.value)} /></div>
            <div><Label className="label-eyebrow">Capacity</Label>
              <Input type="number" className={`${BTN} h-9`} value={form.capacity ?? ""} onChange={(e) => set("capacity", e.target.value)} placeholder="Unlimited" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-eyebrow">Start *</Label>
              <Input type="datetime-local" className={`${BTN} h-9`}
                     value={(form.start_at || "").slice(0, 16)}
                     onChange={(e) => set("start_at", e.target.value ? `${e.target.value}:00Z` : "")}
                     data-testid="session-start" /></div>
            <div><Label className="label-eyebrow">End *</Label>
              <Input type="datetime-local" className={`${BTN} h-9`}
                     value={(form.end_at || "").slice(0, 16)}
                     onChange={(e) => set("end_at", e.target.value ? `${e.target.value}:00Z` : "")}
                     data-testid="session-end" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className={BTN} onClick={onClose}>Cancel</Button>
          <Button className={BTN} onClick={submit} disabled={busy} data-testid="session-save">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═════════════════════════════════════════════════════════════════════
// 4 · Kiosk (QR check-in)
// ═════════════════════════════════════════════════════════════════════

function KioskTab({ wsId, expoId }) {
  const [scan, setScan] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async (token) => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/organizer/expos/${expoId}/kiosk/check-in`,
        { qr_token: token || scan });
      setResult({ ok: true, ...data });
      setScan("");
    } catch (e) {
      setResult({ ok: false, msg: apiErrorMessage(e, "Check-in failed") });
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="kiosk-tab">
      <div className={`${T.wide} p-4`}>
        <div className="text-xs text-muted-foreground mb-2">
          Scan the visitor&apos;s QR (or paste the token) to check them in. Repeat scans are idempotent — you&apos;ll see the original check-in time.
        </div>
        <div className="flex gap-2">
          <Input
            className={`${BTN} h-10 font-mono`}
            placeholder="Paste QR token or scan into this field"
            value={scan}
            autoFocus
            onChange={(e) => setScan(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && scan.trim()) submit(); }}
            data-testid="kiosk-input"
          />
          <Button className={BTN} onClick={() => submit()} disabled={busy || !scan.trim()} data-testid="kiosk-submit">
            {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Check in"}
          </Button>
        </div>
      </div>
      {result && (
        <div className={`${T.wide} p-4 ${result.ok ? "border-emerald-400 bg-emerald-50/40" : "border-rose-400 bg-rose-50/40"}`}
              data-testid="kiosk-result">
          {result.ok ? (
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              <div>
                <div className="text-lg font-bold">{result.ticket?.buyer_name}</div>
                <div className="text-xs text-muted-foreground">
                  {result.ticket?.tier_name}
                  {result.already_checked_in ? (
                    <span className="ml-2 chip text-[10px] bg-amber-100 border-amber-400 text-amber-900">Already checked in · {result.checked_in_at?.slice(11, 16)}</span>
                  ) : (
                    <span className="ml-2 chip text-[10px] bg-emerald-100 border-emerald-400 text-emerald-800">Just checked in · {result.checked_in_at?.slice(11, 16)}</span>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <XCircle className="w-8 h-8 text-rose-600" />
              <div>
                <div className="font-semibold text-rose-800">Check-in failed</div>
                <div className="text-xs text-rose-700">{result.msg}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// 5 · Stall amenities (add-on pricing catalog)
// ═════════════════════════════════════════════════════════════════════

function AmenitiesTab({ wsId, expoId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/workspaces/${wsId}/organizer/expos/${expoId}/amenities`);
      setRows(r.data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load amenities")); }
    finally { setLoading(false); }
  }, [wsId, expoId]);
  useEffect(() => { load(); }, [load]);

  const del = async (a) => {
    if (!window.confirm(`Delete amenity "${a.name}"? Booths already using this add-on keep their price snapshot.`)) return;
    try {
      await api.delete(`/organizer-amenities/${a.id}`);
      toast.success("Amenity deleted"); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  return (
    <div className="space-y-3" data-testid="amenities-tab">
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          Priced add-ons (power, wi-fi, catering, signage…) that organizers can attach to any booth.
        </div>
        <Button size="sm" className={BTN} onClick={() => setEditing("new")} data-testid="amenity-new">
          <Plus className="w-3.5 h-3.5 mr-1" /> New amenity
        </Button>
      </div>
      {loading ? <div className="text-sm text-muted-foreground">Loading…</div> : rows.length === 0 ? (
        <div className="border border-border p-6 text-center text-xs text-muted-foreground italic">
          No amenities yet — create catalog entries you can attach to booths.
        </div>
      ) : (
        <div className={T.wide}>
          <div className={`grid grid-cols-12 gap-2 px-3 py-2 ${T.muted}`}>
            <div className="col-span-2">Code</div>
            <div className="col-span-4">Name</div>
            <div className="col-span-2">Unit</div>
            <div className="col-span-2">Price</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {rows.map((a) => (
            <div key={a.id} className="grid grid-cols-12 gap-2 px-3 py-2 items-center border-t border-border text-sm hover:bg-muted/20"
                  data-testid={`amenity-row-${a.id}`}>
              <div className="col-span-2 font-mono text-xs">{a.code}</div>
              <div className="col-span-4">
                <div className="font-semibold">{a.name}</div>
                {a.description && <div className="text-[11px] text-muted-foreground truncate">{a.description}</div>}
              </div>
              <div className="col-span-2 text-xs">{a.unit || "flat"}</div>
              <div className="col-span-2 font-mono text-xs">{a.currency || "INR"} {Number(a.price_amount).toLocaleString()}</div>
              <div className="col-span-2 flex items-center justify-end gap-1">
                <Button size="sm" variant="outline" className={`${BTN} h-7 text-[10px]`} onClick={() => setEditing(a)}>Edit</Button>
                <Button size="sm" variant="ghost" className={`${BTN} h-7 w-7 p-0 text-muted-foreground hover:text-destructive`} onClick={() => del(a)}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && (
        <AmenityDialog wsId={wsId} expoId={expoId}
                        amenity={editing === "new" ? null : editing}
                        onClose={() => setEditing(null)}
                        onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function AmenityDialog({ wsId, expoId, amenity, onClose, onSaved }) {
  const [form, setForm] = useState(amenity || {
    code: "", name: "", description: "", price_amount: 0,
    currency: "INR", unit: "flat", active: true,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    if (!form.code || !form.name) { toast.error("Code + name required"); return; }
    setBusy(true);
    try {
      const payload = { ...form, price_amount: +form.price_amount };
      if (amenity) await api.patch(`/organizer-amenities/${amenity.id}`, payload);
      else          await api.post(`/workspaces/${wsId}/organizer/expos/${expoId}/amenities`, payload);
      toast.success(amenity ? "Amenity updated" : "Amenity created");
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="amenity-dialog">
        <DialogHeader><DialogTitle>{amenity ? "Edit amenity" : "New amenity"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="label-eyebrow">Code *</Label>
              <Input className={`${BTN} h-9 font-mono`} value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} data-testid="amenity-code" /></div>
            <div className="col-span-2"><Label className="label-eyebrow">Name *</Label>
              <Input className={`${BTN} h-9`} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="amenity-name" /></div>
          </div>
          <div><Label className="label-eyebrow">Description</Label>
            <Textarea className={BTN} rows={2} value={form.description || ""} onChange={(e) => set("description", e.target.value)} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="label-eyebrow">Price *</Label>
              <Input type="number" step="0.01" className={`${BTN} h-9`} value={form.price_amount} onChange={(e) => set("price_amount", e.target.value)} data-testid="amenity-price" /></div>
            <div><Label className="label-eyebrow">Currency</Label>
              <Input className={`${BTN} h-9`} value={form.currency} onChange={(e) => set("currency", e.target.value.toUpperCase())} maxLength={3} /></div>
            <div><Label className="label-eyebrow">Unit</Label>
              <Input className={`${BTN} h-9`} value={form.unit || ""} onChange={(e) => set("unit", e.target.value)} placeholder="flat / per day" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className={BTN} onClick={onClose}>Cancel</Button>
          <Button className={BTN} onClick={submit} disabled={busy} data-testid="amenity-save">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
